import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Checkbox, FormControlLabel } from "@mui/material";
import { useApiUtil } from "@/utils/apiUtil";
import { useCommon } from "@/utils/common";
import TitleLabel from "@/comp/TitleLabel";
import SearchOrderHistorySearchBar from "./searchOrderHistory_SearchBar";
import SearchOrderHistoryTable from "./searchOrderHistory_Table";

const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

const createConditions = (orderNo = "") => ({ orderDate: today(), department: "", modifier: "", orderNo });

const SearchOrderHistoryContent = ({ initialOrderNo = "", open, layoutVersion = 0 }) => {
  const commonJs = useCommon();
  const { gfnCallApi } = useApiUtil();
  const apiRef = useRef(gfnCallApi);
  const conditionsRef = useRef();
  const autoRefreshInProgressRef = useRef(false);
  const [conditions, setConditions] = useState(() => createConditions());
  const [historyRows, setHistoryRows] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [autoUpdate, setAutoUpdate] = useState(false);
  apiRef.current = gfnCallApi;

  const statusLabels = useMemo(() => Object.fromEntries(
    (commonJs.gfnGetComboItem({ listCode: "SM20", addAll: false }) ?? [])
      .map((option) => [String(option.value), option.label]),
  ), [commonJs]);

  // SM24 / 00: 주문이력 자동조회 주기(초). 예) "300초"
  const autoUpdateIntervalMs = useMemo(() => {
    const intervalCode = (commonJs.gfnGetComboItem({ listCode: "SM24", addAll: false }) ?? [])
      .find((option) => String(option.value) === "00");
    const seconds = Number.parseInt(String(intervalCode?.label ?? "").replace(/[^0-9]/g, ""), 10);

    return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 0;
  }, [commonJs]);

  conditionsRef.current = conditions;

  const searchHistory = useCallback(async (searchConditions) => {
    const result = await apiRef.current({
      url: "orderHistoryList",
      data: {
        ORDER_DATE: searchConditions.orderDate?.replaceAll("-", ""),
        DEPARTMENT: searchConditions.department,
        MODIFIER: searchConditions.modifier,
        ORDER_NO: searchConditions.orderNo,
      },
      showMsg: false,
    });
    setHistoryRows(result?.data ?? []);
  }, []);

  useEffect(() => {
    if (!open) return;
    const nextConditions = createConditions(initialOrderNo);
    setConditions(nextConditions);
    searchHistory(nextConditions);
  }, [initialOrderNo, open, searchHistory]);

  useEffect(() => {
    const loadDepartments = async () => {
      const result = await apiRef.current({ url: "deptList", data: {}, showMsg: false });
      setDepartmentOptions((result?.data ?? []).map((department) => ({
        value: department.DEPT_CD,
        label: department.DEPT_KORN_NM,
      })));
    };
    loadDepartments();
  }, []);

  useEffect(() => {
    if (!autoUpdate || !open || !autoUpdateIntervalMs) return undefined;

    const refreshHistory = async () => {
      if (autoRefreshInProgressRef.current) return;

      autoRefreshInProgressRef.current = true;
      try {
        await searchHistory(conditionsRef.current);
      } finally {
        autoRefreshInProgressRef.current = false;
      }
    };

    const timerId = window.setInterval(refreshHistory, autoUpdateIntervalMs);
    return () => window.clearInterval(timerId);
  }, [autoUpdate, autoUpdateIntervalMs, open, searchHistory]);

  const changeCondition = (field) => (event) => setConditions((previous) => ({ ...previous, [field]: event.target.value }));
  const resetConditions = () => {
    const nextConditions = {
      ...createConditions(),
      orderDate: conditions.orderDate,
    };
    setConditions(nextConditions);
    searchHistory(nextConditions);
  };
  const filterByOrderNo = (row) => {
    const nextConditions = { ...conditions, orderNo: String(row.ORDER_NO ?? "") };
    setConditions(nextConditions);
    searchHistory(nextConditions);
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <SearchOrderHistorySearchBar conditions={conditions} departmentOptions={departmentOptions} onChange={changeCondition} onReset={resetConditions} onSearch={() => searchHistory(conditions)} />
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 0.5, mb: 1, minHeight: 24 }}>
        <TitleLabel label="로그 리스트" />
        <FormControlLabel
          control={<Checkbox checked={autoUpdate} onChange={(event) => setAutoUpdate(event.target.checked)} size="small" sx={{ p: 0.25 }} />}
          label="변경사항 자동 업데이트"
          sx={{ m: 0, height: 24, "& .MuiFormControlLabel-label": { fontSize: 12 } }}
        />
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        <SearchOrderHistoryTable rows={historyRows} layoutVersion={layoutVersion} open={open} statusLabels={statusLabels} onOrderClick={filterByOrderNo} />
      </Box>
    </Box>
  );
};

export default SearchOrderHistoryContent;
